import { useEffect, useMemo, useState } from 'react';
import { errorMessage, financeGet, num, pad2, type FinanceCtx } from '../financeApi';
import PrintPortal from '../common/PrintPortal';

type Bulletin = {
  from: string;
  to: string;
  expense: number;
  categories: { category: string; total: number; count: number; donors: string[]; anonymousCount: number }[];
};

function dateValue(d: Date) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** 가장 최근 주일(오늘이 주일이면 오늘)과 그 주 월요일. */
function lastSundayWeek() {
  const sunday = new Date();
  sunday.setDate(sunday.getDate() - sunday.getDay());
  const monday = new Date(sunday);
  monday.setDate(sunday.getDate() - 6);
  return { from: dateValue(monday), to: dateValue(sunday) };
}

// 주보 인쇄용 헌금 요약: 항목별 합계(+선택 시 헌금자 명단). 무명 헌금은 이름 없이 건수만.
export default function BulletinSummary({ ctx }: { ctx: FinanceCtx }) {
  const [period, setPeriod] = useState(lastSundayWeek);
  const [withNames, setWithNames] = useState(true);
  const [withExpense, setWithExpense] = useState(false);
  const [data, setData] = useState<Bulletin | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!period.from || !period.to || period.from > period.to) return;
    setError(null);
    financeGet<Bulletin>(ctx, '/reports/bulletin', period)
      .then(setData)
      .catch((err) => setError(errorMessage(err, '주보용 요약을 만들지 못했습니다.')));
  }, [ctx, period]);

  const total = data?.categories.reduce((s, c) => s + c.total, 0) ?? 0;

  const text = useMemo(() => {
    if (!data) return '';
    const lines = [`■ 헌금 보고 (${data.from.slice(5).replace('-', '/')} ~ ${data.to.slice(5).replace('-', '/')})`];
    data.categories.forEach((c) => {
      lines.push(`· ${c.category} ${num(c.total)}원`);
      if (withNames) {
        const names = [...c.donors, ...(c.anonymousCount > 0 ? [`무명 ${c.anonymousCount}`] : [])];
        if (names.length > 0) lines.push(`  ${names.join(' ')}`);
      }
    });
    lines.push(`합계 ${num(total)}원`);
    if (withExpense) lines.push(`(같은 기간 지출 ${num(data.expense)}원)`);
    return lines.join('\n');
  }, [data, withNames, withExpense, total]);

  const copy = () => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <>
      <div className="manage-filterbar">
        <input type="date" value={period.from} onChange={(e) => setPeriod({ ...period, from: e.target.value })} />
        <span>~</span>
        <input type="date" value={period.to} onChange={(e) => setPeriod({ ...period, to: e.target.value })} />
        <button type="button" className="manage-btn manage-btn--sm" onClick={() => setPeriod(lastSundayWeek())}>지난 주일 주간</button>
        <label className="fin-check"><input type="checkbox" checked={withNames} onChange={(e) => setWithNames(e.target.checked)} />헌금자 명단 포함</label>
        <label className="fin-check"><input type="checkbox" checked={withExpense} onChange={(e) => setWithExpense(e.target.checked)} />지출 합계 포함</label>
        <span className="fin-spacer" />
        <button type="button" className="manage-btn manage-btn--sm" disabled={!data} onClick={copy}>{copied ? '복사됨 ✓' : '글 복사'}</button>
        <button type="button" className="manage-btn manage-btn--sm manage-btn--primary" disabled={!data} onClick={() => setPrinting(true)}>🖨️ 인쇄</button>
      </div>
      {error && <div className="manage-state manage-state--error">{error}</div>}
      {data && (
        <div className="fin-doc-preview">
          {data.categories.length === 0 ? (
            <p className="fin-muted">이 기간에 등록된 헌금이 없습니다.</p>
          ) : (
            <pre className="fin-bulletin">{text}</pre>
          )}
        </div>
      )}
      {printing && (
        <PrintPortal title="주보_헌금보고" onDone={() => setPrinting(false)}>
          <pre className="fin-bulletin fin-bulletin--print">{text}</pre>
        </PrintPortal>
      )}
    </>
  );
}
